import {
  Anchor as AnchorIcon,
  ArrowLeft,
  BookOpen,
  Calendar,
  Check,
  Clock,
  Copy,
  ExternalLink,
  Globe,
  Paperclip,
  Sparkles,
  StickyNote,
  Trash2,
  User,
} from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { toggleTaskStatusKey } from '../../lib/task-list';
import { type RouterOutputs, trpc } from '../../lib/trpc';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { EntityCommentThread } from '../comments/EntityCommentThread';
import { EmptyState } from '../empty-state';
import { NotesPanel } from '../notes/NotesPanel';
import { TaskPanel } from '../tasks/TaskPanel';
import { FEATURE_TITLE_MAP, FeatureIconBar } from './FeatureIconBar';
import { LinkList } from './LinkList';

export type AnchorItem = RouterOutputs['anchors']['getById'];

export interface SharedAnchorPageProps {
  anchor: AnchorItem;
  workspaceId: string;
  orgId: string;
  onBack?: () => void;
  onDelete?: () => void;
  onNavigateToTask?: (taskId: number) => void;
  isReadOnly?: boolean;
}

export function SharedAnchorPage({
  anchor,
  workspaceId,
  orgId,
  onBack,
  onDelete,
  onNavigateToTask,
  isReadOnly = false,
}: SharedAnchorPageProps) {
  const [copiedUrl, setCopiedUrl] = React.useState(false);
  const [activeFeature, setActiveFeature] = React.useState<string>('tasks');

  const utils = trpc.useUtils();

  // Queries for skills
  const tasksQuery = trpc.tasks.getByAnchor.useQuery({ anchorId: anchor.id });
  const anchorTasks = tasksQuery.data || [];

  const statusesQuery = trpc.taskStatuses.list.useQuery();
  const statuses = statusesQuery.data?.items || [];

  const membersQuery = trpc.workspace.listMembers.useQuery();
  const members = membersQuery.data || [];

  const updateTaskMutation = trpc.tasks.update.useMutation({
    onSuccess: () => {
      utils.tasks.getByAnchor.invalidate({ anchorId: anchor.id });
      utils.tasks.getAll.invalidate();
      utils.anchors.getPageMetrics.invalidate({ anchorId: anchor.id });
    },
  });

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(anchor.url);
    setCopiedUrl(true);
    toast.success('URL copied to clipboard');
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  const handleLaunchBrowser = () => {
    window.open(anchor.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="flex flex-col h-full w-full bg-background overflow-hidden">
      {/* Top Header Banner */}
      <div className="border-b border-border bg-card/60 backdrop-blur-sm px-6 py-4 shrink-0">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Title & Domain Info */}
          <div className="flex items-start gap-3.5 min-w-0">
            {onBack && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onBack}
                className="h-11 w-11 rounded-xl shrink-0 md:hidden text-muted-foreground hover:text-foreground"
                title="Back to Anchors Directory"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
            <div className="h-11 w-11 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center shrink-0 mt-0.5">
              <AnchorIcon className="h-5 w-5 text-sky-600 dark:text-sky-400" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-bold tracking-tight truncate">{anchor.label}</h1>
                <Badge variant="secondary" className="font-mono text-xs font-normal">
                  {anchor.domain}
                </Badge>
                {anchor.contactName && (
                  <Badge variant="outline" className="text-xs gap-1 border-primary/30 text-primary">
                    <User className="h-3 w-3" />
                    {anchor.contactName}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground truncate">
                <span className="truncate">{anchor.url}</span>
                <button
                  type="button"
                  onClick={handleCopyUrl}
                  className="p-1 hover:text-foreground transition-colors shrink-0"
                  title="Copy URL"
                >
                  {copiedUrl ? (
                    <Check className="h-3.5 w-3.5 text-green-600" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Header Actions */}
          <div className="flex items-center gap-2.5 shrink-0">
            <Button
              onClick={handleLaunchBrowser}
              className="gap-2 font-medium shadow-sm bg-primary text-primary-foreground hover:bg-primary/90"
              title="Opens target page in a new browser tab with Skill Viper extension"
            >
              <ExternalLink className="h-4 w-4" />
              Launch in Browser
            </Button>
            {!isReadOnly && onDelete && (
              <Button
                variant="outline"
                size="icon"
                onClick={onDelete}
                className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 border-border"
                title="Delete Anchor"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Dual-Column Content Body */}
      <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden">
        {/* Left Column: Anchor Overview & Observation Details */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6 lg:p-8 space-y-6 scrollbar-thin">
          {/* Target URL Card */}
          <div className="rounded-xl border border-border/60 bg-card p-5 space-y-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold flex items-center gap-2">
                <Globe className="h-4 w-4 text-muted-foreground" />
                Target Web Page
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLaunchBrowser}
                className="text-xs text-primary gap-1.5 h-8 px-2"
              >
                Open live page
                <ExternalLink className="h-3 w-3" />
              </Button>
            </div>
            <div className="rounded-lg bg-muted/40 p-3 text-xs font-mono break-all select-all border border-border/40">
              {anchor.url}
            </div>
            <p className="text-xs text-muted-foreground">
              When launching this URL in your browser, the Skill Viper Chrome extension will
              automatically attach and load contextual notes, tasks, and discussion.
            </p>
          </div>

          {/* Linked Contact Card */}
          <div className="rounded-xl border border-border/60 bg-card p-5 space-y-3 shadow-sm">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              Associated Contact
            </h2>
            {anchor.contactId && anchor.contactName ? (
              <div className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-muted/20">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm">
                    {anchor.contactName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <Link
                      to={`/org/${orgId}/workspace/${workspaceId}/contacts/${anchor.contactId}`}
                      className="font-medium text-sm text-primary hover:underline"
                    >
                      {anchor.contactName}
                    </Link>
                    <p className="text-xs text-muted-foreground">Primary Contact Record</p>
                  </div>
                </div>
                <Link
                  to={`/org/${orgId}/workspace/${workspaceId}/contacts/${anchor.contactId}`}
                  className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                >
                  View Profile
                  <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">
                This anchor is not currently linked to a specific contact profile.
              </p>
            )}
          </div>

          {/* Observations & AI Context Card */}
          <div className="rounded-xl border border-border/60 bg-card p-5 space-y-3 shadow-sm">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-sky-500" />
              Observation & AI Context
            </h2>
            {anchor.lastObservation || anchor.lastContext ? (
              <div className="space-y-3 text-sm">
                {anchor.lastObservation && (
                  <div>
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                      Latest Observation
                    </h3>
                    <div className="p-3 rounded-lg bg-muted/40 text-xs leading-relaxed border border-border/40 whitespace-pre-wrap">
                      {anchor.lastObservation}
                    </div>
                  </div>
                )}
                {anchor.lastContext && (
                  <div>
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                      Context Summary
                    </h3>
                    <div className="p-3 rounded-lg bg-muted/40 text-xs leading-relaxed border border-border/40 whitespace-pre-wrap">
                      {anchor.lastContext}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 rounded-lg bg-muted/20 text-center space-y-1 border border-dashed border-border/60">
                <p className="text-xs text-muted-foreground">
                  No automated observations recorded yet.
                </p>
                <p className="text-[11px] text-muted-foreground/80">
                  Observations captured by the extension AI analyzer will sync here automatically.
                </p>
              </div>
            )}
          </div>

          {/* Timestamps */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground px-1">
            <span className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              Created {new Date(anchor.createdAt * 1000).toLocaleDateString()}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              Updated {new Date(anchor.updatedAt * 1000).toLocaleDateString()}
            </span>
          </div>
        </div>

        {/* Right Column: Canonical Side Panel matching Extension Layout */}
        <div className="w-full lg:w-[440px] xl:w-[480px] border-t lg:border-t-0 lg:border-l border-border bg-card/40 flex flex-col shrink-0 min-h-0">
          {/* FeatureIconBar - canonical extension skill icon bar */}
          <div className="shrink-0 bg-muted/20">
            <FeatureIconBar
              anchorId={anchor.id}
              activeFeature={activeFeature}
              onSelectFeature={setActiveFeature}
            />
          </div>

          {/* Feature Header Strip - matching extension */}
          <div className="px-3 py-2 shrink-0 border-b border-border bg-slate-100/90 dark:bg-slate-900/90 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-3.5 rounded-full bg-primary" />
              <span className="text-xs font-bold uppercase tracking-wider text-foreground">
                {FEATURE_TITLE_MAP[activeFeature] ?? 'Feature'}
              </span>
            </div>
          </div>

          {/* Feature Panel Content */}
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {activeFeature === 'tasks' && (
              <TaskPanel
                tasks={anchorTasks}
                statuses={statuses}
                members={members}
                isLoading={tasksQuery.isLoading}
                isReadOnly={isReadOnly}
                createContext={{ anchorId: anchor.id }}
                onTaskClick={(taskId) => {
                  if (onNavigateToTask) onNavigateToTask(taskId);
                }}
                onToggleComplete={(task) => {
                  if (isReadOnly) return;
                  const nextStatus = toggleTaskStatusKey(statuses, task.status);
                  updateTaskMutation.mutate({ id: task.id, status: nextStatus });
                }}
              />
            )}

            {activeFeature === 'notes' && (
              <NotesPanel
                entityType="anchor"
                entityId={anchor.id}
                isReadOnly={isReadOnly}
                composerPosition="bottom"
                placeholder="Capture a note about this page…"
                emptyState={
                  <div className="flex min-h-0 flex-1 items-center justify-center">
                    <EmptyState
                      icon={StickyNote}
                      title="No notes yet"
                      description={
                        isReadOnly
                          ? 'Nobody has captured a note for this page.'
                          : 'Keep useful details close to the page.'
                      }
                    />
                  </div>
                }
                className="h-full overflow-hidden px-2 pb-2"
              />
            )}

            {activeFeature === 'threads' && (
              <EntityCommentThread
                target={{ type: 'anchor', anchorId: anchor.id }}
                isReadOnly={isReadOnly}
              />
            )}

            {activeFeature === 'links' && <LinkList anchorId={anchor.id} isReadOnly={isReadOnly} />}

            {activeFeature === 'chat' && (
              <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
                <div className="rounded-xl border border-border/80 bg-background/80 p-4 space-y-3 shadow-xs">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <h3 className="text-xs font-semibold text-foreground">AI Page Copilot</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    The AI Copilot directly inspects live DOM elements and page content when you
                    browse this page using the Skill Viper browser extension.
                  </p>
                  <Button
                    size="sm"
                    variant="default"
                    onClick={handleLaunchBrowser}
                    className="w-full text-xs gap-1.5"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Open Page to Chat with Copilot
                  </Button>
                </div>

                {anchor.lastObservation && (
                  <div className="rounded-xl border border-border/60 bg-muted/20 p-3 space-y-1.5 text-xs">
                    <p className="font-semibold text-foreground text-[11px] uppercase tracking-wider">
                      Recorded Page Knowledge
                    </p>
                    <p className="text-muted-foreground leading-normal whitespace-pre-wrap">
                      {anchor.lastObservation}
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeFeature === 'docs' && (
              <div className="flex-1 min-h-0 flex items-center justify-center p-6">
                <EmptyState
                  icon={BookOpen}
                  title="No documentation yet"
                  description="Reference guides and documentation attached to this anchor will appear here."
                />
              </div>
            )}

            {activeFeature === 'files' && (
              <div className="flex-1 min-h-0 flex items-center justify-center p-6">
                <EmptyState
                  icon={Paperclip}
                  title="No attached files"
                  description="Files and media uploaded to this anchor will appear here."
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
